import type { ObjectiveStatus } from '../lib/api';

/**
 * El estado de un objetivo se lee por su forma, no por su color. Cada estado
 * tiene un glifo propio, dibujado en una caja de 16. Es el elemento firma:
 * un vistazo a la columna de marcas y sabes qué tienes vivo, qué está cerrado
 * y qué se rompió.
 */

type Tone = 'active' | 'gated' | 'broken' | 'dormant';

interface Meta {
  label: string;
  tone: Tone;
}

export const STATUS_META: Record<ObjectiveStatus, Meta> = {
  active: { label: 'Activo', tone: 'active' },
  locked_plan: { label: 'Per plan', tone: 'gated' },
  needs_setup: { label: 'Por configurar', tone: 'gated' },
  no_permission: { label: 'Sin permiso', tone: 'gated' },
  license_missing: { label: 'Falta licencia', tone: 'gated' },
  license_expired: { label: 'Licencia caducada', tone: 'broken' },
  plan_suspended: { label: 'Plan suspendido', tone: 'broken' },
  not_applicable: { label: 'No aplica', tone: 'dormant' },
};

function Mark({ status }: { status: ObjectiveStatus }) {
  switch (status) {
    case 'active':
      // Rombo lleno: capacidad concedida.
      return <path d="M8 2.5 13.5 8 8 13.5 2.5 8 8 2.5Z" fill="currentColor" stroke="none" />;
    case 'locked_plan':
      // Cuadro con flecha ascendente: alcanzable subiendo de plan.
      return (
        <>
          <rect x="3" y="3" width="10" height="10" rx="2" />
          <path d="M6.2 9.8 9.8 6.2M6.8 6.2h3v3" />
        </>
      );
    case 'needs_setup':
      // Cuadro punteado: falta un paso de configuración.
      return <rect x="3" y="3" width="10" height="10" rx="2" strokeDasharray="2.2 2" />;
    case 'no_permission':
      // Candado: tu rol no llega.
      return (
        <>
          <rect x="3.5" y="7.5" width="9" height="6" rx="1.4" />
          <path d="M5.5 7.5V6a2.5 2.5 0 0 1 5 0v1.5" />
        </>
      );
    case 'license_missing':
      // Documento vacío: falta aportar la licencia.
      return <path d="M9 2.5v3h3M4.5 13.5V4a1.5 1.5 0 0 1 1.5-1.5h3l3 3v8a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5Z" />;
    case 'license_expired':
      // Documento tachado: la licencia venció.
      return (
        <>
          <path d="M9 2.5v3h3M4.5 13.5V4a1.5 1.5 0 0 1 1.5-1.5h3l3 3v8a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5Z" />
          <path d="M3 13 13 3" />
        </>
      );
    case 'plan_suspended':
      // Pausa: la suscripción está detenida.
      return (
        <>
          <rect x="4" y="3.5" width="3" height="9" rx="1" />
          <rect x="9" y="3.5" width="3" height="9" rx="1" />
        </>
      );
    case 'not_applicable':
      // Trazo: no forma parte de tu actividad.
      return <path d="M4 8h8" />;
  }
}

export function StatusGlyph({ status, size = 15 }: { status: ObjectiveStatus; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <Mark status={status} />
    </svg>
  );
}
